import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye, EyeOff, User, Lock, Globe, ChevronDown, X } from 'lucide-react'
import { supabase } from '@/lib/supabase'

export default function Login() {
  const navigate = useNavigate()
  const [form, setForm] = useState({ username: '', password: '', remember: true })
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [error, setError] = useState('')
  const [showBanner, setShowBanner] = useState(true)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError('')

    const { data: email, error: rpcError } = await supabase.rpc('get_user_email_by_username', {
      p_username: form.username.trim().toLowerCase(),
    })

    if (rpcError || !email) {
      setError('Nama pengguna atau password salah.')
      setLoading(false)
      return
    }

    const { error: authError } = await supabase.auth.signInWithPassword({
      email: email as string,
      password: form.password,
    })

    setLoading(false)

    if (authError) {
      setError('Nama pengguna atau password salah.')
      return
    }

    navigate('/app/dashboard')
  }

  async function handleGoogleLogin() {
    setGoogleLoading(true)
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/app/dashboard` },
    })
    setGoogleLoading(false)
  }

  return (
    <div className="min-h-screen flex bg-cerah-sage">
      {showBanner && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-amber-50 border border-amber-200 text-amber-900 text-sm px-4 py-2.5 rounded-xl shadow-md max-w-sm w-[calc(100%-2rem)]">
          <span className="flex-1">
            Untuk masuk, gunakan username: <strong>admin</strong>, password: <strong>Cerah@2024!</strong>
          </span>
          <button
            onClick={() => setShowBanner(false)}
            className="shrink-0 text-amber-500 hover:text-amber-700 transition-colors"
            aria-label="Tutup"
          >
            <X size={15} />
          </button>
        </div>
      )}
      {/* ── Left panel ── */}
      <div className="hidden lg:flex lg:w-1/2 relative flex-col p-10 overflow-hidden">
        {/* Subtle grid bg pattern */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(13,120,120,0.05) 1px, transparent 1px),
              linear-gradient(90deg, rgba(13,120,120,0.05) 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
          }}
        />

        {/* Logo */}
        <div className="relative flex items-center gap-2">
          <CerahLogo />
        </div>

        {/* Headline */}
        <div className="relative flex-1 flex flex-col justify-center">
          <p className="text-cerah-navy/70 text-lg mb-1">Selamat datang di</p>
          <h1 className="text-cerah-navy text-5xl font-bold leading-tight">
            CERAH{' '}
            <span className="text-cerah-teal">Pedia</span>
          </h1>
          <p className="text-cerah-navy/60 text-sm font-medium mt-2 mb-4">
            Knowledge Management – CERAH
          </p>
          <p className="text-cerah-navy/50 text-sm max-w-xs leading-relaxed">
            Satu platform untuk berbagi pengetahuan, informasi, dan kolaborasi untuk transisi energi berkeadilan.
          </p>
        </div>

        {/* Illustration */}
        <div className="relative h-56 flex items-end">
          <RenewableIllustration />
        </div>
      </div>

      {/* ── Right panel ── */}
      <div className="flex-1 flex flex-col bg-white relative">
        {/* Language switcher */}
        <div className="absolute top-5 right-5 flex items-center gap-1 text-sm text-gray-500 cursor-pointer hover:text-gray-700 select-none">
          <Globe size={15} />
          <span>Bahasa</span>
          <ChevronDown size={14} />
        </div>

        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-sm">
            <h2 className="text-2xl font-bold text-cerah-navy mb-1">Masuk ke CERAH Pedia</h2>
            <p className="text-gray-500 text-sm mb-7">Silakan masuk untuk melanjutkan ke dashboard.</p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              {/* Username */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Nama Pengguna
                </label>
                <div className="relative">
                  <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Masukkan nama pengguna"
                    value={form.username}
                    onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                    className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cerah-teal/20 focus:border-cerah-teal transition-colors"
                    required
                    autoComplete="username"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Masukkan password"
                    value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    className="w-full pl-9 pr-10 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cerah-teal/20 focus:border-cerah-teal transition-colors"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              {/* Remember + Forgot */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={form.remember}
                    onChange={e => setForm(f => ({ ...f, remember: e.target.checked }))}
                    className="w-4 h-4 rounded accent-cerah-teal"
                  />
                  <span className="text-sm text-gray-600">Ingat saya</span>
                </label>
                <button type="button" className="text-sm text-cerah-teal hover:underline font-medium">
                  Lupa password?
                </button>
              </div>

              {error && (
                <p className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-md">{error}</p>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={!form.username || !form.password || loading}
                className="w-full py-3 rounded-lg font-semibold text-sm bg-cerah-teal text-white hover:bg-cerah-teal/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors mt-1"
              >
                {loading ? 'Memproses...' : 'Masuk'}
              </button>

              {/* Divider */}
              <div className="flex items-center gap-3 my-1">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-gray-400">atau masuk dengan</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              {/* Google SSO */}
              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={googleLoading}
                className="w-full py-3 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2.5 transition-colors disabled:opacity-40"
              >
                <GoogleIcon />
                {googleLoading ? 'Mengalihkan...' : 'Masuk dengan Google'}
              </button>
            </form>

            <p className="text-center text-sm text-gray-500 mt-7">
              Belum punya akun?{' '}
              <a
                href="mailto:admin@cerah.id"
                className="text-cerah-teal font-medium hover:underline"
              >
                Hubungi administrator CERAH
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

function CerahLogo() {
  return (
    <div className="flex items-center gap-2">
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
        <circle cx="18" cy="18" r="18" fill="#0d7878" />
        <circle cx="18" cy="18" r="7" fill="white" fillOpacity="0.9" />
        {[0, 45, 90, 135, 180, 225, 270, 315].map((deg, i) => (
          <line
            key={i}
            x1="18" y1="4"
            x2="18" y2="8"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            transform={`rotate(${deg} 18 18)`}
          />
        ))}
      </svg>
      <span className="text-cerah-navy font-extrabold text-xl tracking-wider">CERAH</span>
    </div>
  )
}

function RenewableIllustration() {
  return (
    <svg viewBox="0 0 480 220" className="w-full" aria-hidden>
      {/* Sky gradient */}
      <defs>
        <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#b8dbd9" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#eef3ef" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="ground" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#7ec8a0" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#5ab082" stopOpacity="0.8" />
        </linearGradient>
      </defs>

      {/* Ground */}
      <ellipse cx="240" cy="210" rx="260" ry="30" fill="url(#ground)" />

      {/* Wind turbine 1 */}
      <g transform="translate(80,100)">
        <rect x="-2" y="0" width="4" height="100" rx="2" fill="#0d7878" opacity="0.7" />
        <g transform="rotate(30)">
          <ellipse cx="0" cy="-28" rx="4" ry="28" fill="#0d7878" opacity="0.6" />
        </g>
        <g transform="rotate(150)">
          <ellipse cx="0" cy="-28" rx="4" ry="28" fill="#0d7878" opacity="0.6" />
        </g>
        <g transform="rotate(270)">
          <ellipse cx="0" cy="-28" rx="4" ry="28" fill="#0d7878" opacity="0.6" />
        </g>
        <circle r="5" fill="white" stroke="#0d7878" strokeWidth="2" />
      </g>

      {/* Wind turbine 2 */}
      <g transform="translate(150,130)">
        <rect x="-2" y="0" width="4" height="75" rx="2" fill="#0d7878" opacity="0.6" />
        <g transform="rotate(-20)">
          <ellipse cx="0" cy="-22" rx="3" ry="22" fill="#0d7878" opacity="0.5" />
        </g>
        <g transform="rotate(100)">
          <ellipse cx="0" cy="-22" rx="3" ry="22" fill="#0d7878" opacity="0.5" />
        </g>
        <g transform="rotate(220)">
          <ellipse cx="0" cy="-22" rx="3" ry="22" fill="#0d7878" opacity="0.5" />
        </g>
        <circle r="4" fill="white" stroke="#0d7878" strokeWidth="1.5" />
      </g>

      {/* Solar panels */}
      <g transform="translate(290,160)">
        {[0, 45, 90].map((x, i) => (
          <g key={i} transform={`translate(${x}, 0) rotate(-15)`}>
            <rect x="-18" y="-10" width="36" height="20" rx="2" fill="#1a9090" opacity="0.7" />
            <line x1="-18" y1="0" x2="18" y2="0" stroke="white" strokeWidth="0.5" opacity="0.5" />
            <line x1="0" y1="-10" x2="0" y2="10" stroke="white" strokeWidth="0.5" opacity="0.5" />
          </g>
        ))}
      </g>

      {/* City silhouette */}
      <g opacity="0.25" fill="#0a2744">
        <rect x="350" y="120" width="20" height="75" />
        <rect x="375" y="140" width="15" height="55" />
        <rect x="395" y="130" width="25" height="65" />
        <rect x="425" y="150" width="18" height="45" />
        <rect x="448" y="145" width="22" height="50" />
        <rect x="355" y="110" width="10" height="15" />
        <rect x="400" y="118" width="8" height="15" />
      </g>

      {/* Trees */}
      {[210, 235, 255].map((x, i) => (
        <g key={i} transform={`translate(${x},${165 + i * 3})`}>
          <rect x="-2" y="0" width="4" height="20" fill="#3a7a3a" opacity="0.5" />
          <ellipse cx="0" cy="-10" rx={10 - i} ry={14 - i} fill="#4a9a4a" opacity="0.5" />
        </g>
      ))}
    </svg>
  )
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.859-3.048.859-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" />
      <path fill="#FBBC05" d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z" />
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
    </svg>
  )
}
